export type ResolvedAssistantHref =
  | { kind: "internal"; to: string }
  | { kind: "external"; href: string }
  | { kind: "invalid" };

const SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z\d+.-]*:/;
const SAFE_EXTERNAL_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function normalizeOrigin(origin: string | undefined): string | null {
  if (!origin) return null;
  try {
    return new URL(origin).origin;
  } catch {
    return null;
  }
}

function collapseRelative(path: string): string {
  const match = path.match(/^([^?#]*)(.*)$/);
  const pathname = match ? match[1] : path;
  const rest = match ? match[2] : "";
  const segments: string[] = [];
  for (const segment of pathname.split("/")) {
    if (segment === "" || segment === ".") continue;
    if (segment === "..") {
      segments.pop();
      continue;
    }
    segments.push(segment);
  }
  return `/${segments.join("/")}${rest}`;
}

export function resolveAssistantHref(
  href: string | undefined | null,
  origin?: string,
): ResolvedAssistantHref {
  const raw = (href ?? "").trim();
  if (!raw) return { kind: "invalid" };

  if (raw.startsWith("#")) return { kind: "invalid" };

  if (raw.startsWith("//")) {
    const ownOrigin = normalizeOrigin(origin);
    try {
      const url = new URL(`https:${raw}`);
      if (ownOrigin && new URL(ownOrigin).host === url.host) {
        return { kind: "internal", to: `${url.pathname}${url.search}${url.hash}` };
      }
      return { kind: "external", href: url.toString() };
    } catch {
      return { kind: "invalid" };
    }
  }

  if (raw.startsWith("/")) return { kind: "internal", to: collapseRelative(raw) };

  if (SCHEME_PATTERN.test(raw)) {
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      return { kind: "invalid" };
    }
    if (!SAFE_EXTERNAL_PROTOCOLS.has(url.protocol)) return { kind: "invalid" };
    const ownOrigin = normalizeOrigin(origin);
    if (ownOrigin && url.origin === ownOrigin) {
      return { kind: "internal", to: `${url.pathname}${url.search}${url.hash}` };
    }
    return { kind: "external", href: raw };
  }

  return { kind: "internal", to: collapseRelative(raw) };
}

function extractUuidSegment(
  pathname: string | undefined | null,
  pattern: RegExp,
): string | null {
  if (!pathname) return null;
  const match = pathname.match(pattern);
  if (!match) return null;
  let candidate: string;
  try {
    candidate = decodeURIComponent(match[1]);
  } catch {
    return null;
  }
  return UUID_PATTERN.test(candidate) ? candidate.toLowerCase() : null;
}

export function extractOrderIdFromPath(pathname: string | undefined | null): string | null {
  return extractUuidSegment(pathname, /^\/orders\/([^/?#]+)\/?$/);
}

export function extractUserIdFromPath(pathname: string | undefined | null): string | null {
  return extractUuidSegment(pathname, /^\/users\/edit\/([^/?#]+)\/?$/);
}

export interface AssistantPageContext {
  path: string;
  orderId?: string;
  userId?: string;
}

export function buildAssistantContext(pathname: string | undefined | null): AssistantPageContext {
  const path = pathname || "/";
  const orderId = extractOrderIdFromPath(path);
  if (orderId) return { path, orderId };
  const userId = extractUserIdFromPath(path);
  if (userId) return { path, userId };
  return { path };
}

export type AssistantPageKind =
  | "order_detail"
  | "orders_list"
  | "user_detail"
  | "users_list"
  | "other";

export function getAssistantPageKind(pathname: string | undefined | null): AssistantPageKind {
  if (extractOrderIdFromPath(pathname)) return "order_detail";
  if (pathname && /^\/orders\/?$/.test(pathname)) return "orders_list";
  if (extractUserIdFromPath(pathname)) return "user_detail";
  if (pathname && /^\/users\/?$/.test(pathname)) return "users_list";
  return "other";
}

export interface SuggestionCapabilities {
  orders: boolean;
  users: boolean;
}

type SuggestionScope = keyof SuggestionCapabilities;

interface Suggestion {
  text: string;
  requires: SuggestionScope | SuggestionScope[];
}

const MAX_SUGGESTIONS = 4;

const ORDERS_ONLY: SuggestionCapabilities = { orders: true, users: false };

const ORDER_DETAIL_PROMPTS: Suggestion[] = [
  { text: "Summarize this order", requires: "orders" },
  { text: "Why might this order be delayed?", requires: "orders" },
  { text: "Show this customer's other orders", requires: "orders" },
  { text: "Show the payment history for this order", requires: "orders" },
];

const ORDERS_LIST_PROMPTS: Suggestion[] = [
  { text: "Which paid orders are stuck for over 24h?", requires: "orders" },
  { text: "Today's orders by status", requires: "orders" },
  { text: "Show the 10 largest orders this week", requires: "orders" },
  { text: "Which shipped orders haven't been delivered in 5 days?", requires: "orders" },
];

const USER_DETAIL_PROMPTS: Suggestion[] = [
  { text: "Summarize this customer", requires: "users" },
  { text: "What's in this customer's cart?", requires: "users" },
  { text: "Show this customer's recent activity", requires: "users" },
  { text: "Show this customer's orders", requires: "orders" },
];

const USERS_LIST_PROMPTS: Suggestion[] = [
  { text: "Who signed up this week?", requires: "users" },
  { text: "Who signed up this month but hasn't ordered yet?", requires: "users" },
  { text: "Top customers by lifetime value this year", requires: ["users", "orders"] },
];

const GENERAL_PROMPTS: Suggestion[] = [
  { text: "Give me a fulfillment brief for today", requires: "orders" },
  { text: "Which paid orders are stuck for over 24h?", requires: "orders" },
  { text: "Who signed up this week?", requires: "users" },
  { text: "Look up a customer by phone number", requires: "users" },
  { text: "Order stats for the last 7 days", requires: "orders" },
  { text: "Find orders for a customer by phone number", requires: "orders" },
  { text: "New customer stats for the last 30 days", requires: "users" },
  { text: "Who signed up this month but hasn't ordered yet?", requires: "users" },
];

const PAGE_PROMPTS: Record<AssistantPageKind, Suggestion[]> = {
  order_detail: ORDER_DETAIL_PROMPTS,
  orders_list: ORDERS_LIST_PROMPTS,
  user_detail: USER_DETAIL_PROMPTS,
  users_list: USERS_LIST_PROMPTS,
  other: GENERAL_PROMPTS,
};

function selectPrompts(
  suggestions: Suggestion[],
  capabilities: SuggestionCapabilities,
): string[] {
  return suggestions
    .filter((suggestion) =>
      ([] as SuggestionScope[])
        .concat(suggestion.requires)
        .every((scope) => capabilities[scope] === true),
    )
    .slice(0, MAX_SUGGESTIONS)
    .map((suggestion) => suggestion.text);
}

export function getSuggestedPrompts(
  pathname: string | undefined | null,
  capabilities: SuggestionCapabilities = ORDERS_ONLY,
): string[] {
  const pagePrompts = selectPrompts(PAGE_PROMPTS[getAssistantPageKind(pathname)], capabilities);
  if (pagePrompts.length > 0) return pagePrompts;
  return selectPrompts(GENERAL_PROMPTS, capabilities);
}
